import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Play, Lock, ArrowLeft, ChevronLeft, ChevronRight, BookOpen, FileText, Map, X, Linkedin, Instagram, Mail, Link2, Check, Share2 } from 'lucide-react';
import { articles } from '../mockData';
import ArticleSlideshow from '../components/ArticleSlideshow';
import LuxeCompassWatermark from '../components/LuxeCompassWatermark';
import '../styles/CompassPreview.css';

// IDs of guides available in pre-launch (6 guides)
// Order reflects inclusive-of-men priority per user request:
// 108: Missing Diagnosis / Impossible Math (FIRST — brand hero)
// 19: Low Testosterone (Men's Health — hormones)
// 18: Insomnia (Men + Women — sleep)
// 6: Hormonal Health incl. Thyroid (Women's Health)
// 15: Stubborn Weight Gain (Weight Loss)
// 103: Signs Your Symptoms Are Being Dismissed (LAST — the gateway advocacy story)
const UNLOCKED_GUIDE_IDS = [108, 19, 18, 6, 15, 103];

// IDs of articles available in pre-launch
// 22: The Story of the Missing Diagnosis and the Impossible Math (FIRST — matches guide)
// 2:  Hypothyroidism: Beyond the TSH Test (Labs/Testing)
const UNLOCKED_ARTICLE_IDS = [22, 2];

const CompassPreview = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // Compass shares its component with /articles inside CurtainNavigation, so
  // honor whichever path the visitor is actually on as the SEO-active page.
  const isActiveRoute = ['/compass-preview', '/articles'].includes(location.pathname);
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [activeTab, setActiveTab] = useState('guides');
  const [openedFromUrl, setOpenedFromUrl] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  
  // Filter interactive guides (slideshows) and regular articles
  const allGuides = articles.filter(a => a.isSlideshow);
  const allArticles = articles.filter(a => !a.isSlideshow && !a.deepDive && a.content);
  
  // Separate unlocked and locked content - preserve UNLOCKED_GUIDE_IDS order
  const unlockedGuides = UNLOCKED_GUIDE_IDS
    .map(id => allGuides.find(g => g.id === id))
    .filter(Boolean);
  const lockedGuides = allGuides.filter(g => !UNLOCKED_GUIDE_IDS.includes(g.id));
  const unlockedArticles = UNLOCKED_ARTICLE_IDS
    .map(id => allArticles.find(a => a.id === id))
    .filter(Boolean);
  const lockedArticles = allArticles.filter(a => !UNLOCKED_ARTICLE_IDS.includes(a.id));
  
  // Handle URL parameters to auto-open shared guides/articles
  useEffect(() => {
    const guideId = searchParams.get('guide');
    const articleId = searchParams.get('article');
    
    if (guideId) {
      const guideIdNum = parseInt(guideId);
      const guide = articles.filter(a => a.isSlideshow).find(g => g.id === guideIdNum);
      if (guide && UNLOCKED_GUIDE_IDS.includes(guide.id)) {
        setSelectedGuide(guide);
        setOpenedFromUrl(true);  // Mark as opened from URL
        setActiveTab('guides');
      }
    } else if (articleId) {
      const articleIdNum = parseInt(articleId);
      const article = articles.filter(a => !a.isSlideshow && !a.deepDive && a.content).find(a => a.id === articleIdNum);
      if (article && UNLOCKED_ARTICLE_IDS.includes(article.id)) {
        setSelectedArticle(article);
        setActiveTab('articles');
      }
    }
  }, [searchParams]);
  
  const handleGuideClick = (guide) => {
    if (UNLOCKED_GUIDE_IDS.includes(guide.id)) {
      setSelectedGuide(guide);
      setOpenedFromUrl(false);  // Normal click, not from URL
    }
  };

  const handleArticleClick = (article) => {
    if (UNLOCKED_ARTICLE_IDS.includes(article.id)) {
      setSelectedArticle(article);
    }
  };

  // Long-tail SEO keywords for each guide
  const guideSeoData = {
    19: { // Low Testosterone
      title: "Low Testosterone Signs Dismissed by Doctors | CADENCE Compass",
      description: "Fatigue, low energy, mood changes dismissed? Learn about low testosterone symptoms doctors often overlook. Free guide for men whose symptoms aren't taken seriously.",
      keywords: "low testosterone dismissed, doctor won't test testosterone, fatigue not taken seriously, men's health advocacy"
    },
    6: { // Hormonal Health / Thyroid
      title: "Thyroid Symptoms Dismissed? Doctor Says Labs Are Normal | CADENCE",
      description: "Thyroid symptoms but 'normal' labs? You're not crazy. Learn why TSH alone isn't enough and how to advocate for complete thyroid testing. Free guide.",
      keywords: "thyroid symptoms dismissed, normal TSH still feel bad, doctor won't treat thyroid, hypothyroid symptoms ignored"
    },
    103: { // Medical Gaslighting
      title: "Doctor Dismissed My Symptoms | Signs of Medical Gaslighting",
      description: "Told it's 'just anxiety' or 'in your head'? Recognize medical gaslighting and learn to advocate for yourself. Free guide for patients who feel unheard.",
      keywords: "doctor dismissed my symptoms, medical gaslighting, doctor doesn't believe me, symptoms not taken seriously, patient advocacy"
    },
    15: { // Weight Gain
      title: "Unexplained Weight Gain Doctor Can't Explain | CADENCE Compass",
      description: "Gaining weight despite diet and exercise? Doctor blaming lifestyle? Learn about hidden hormonal causes and how to get proper testing. Free guide.",
      keywords: "unexplained weight gain, doctor blames diet, weight gain hormones, can't lose weight doctor, metabolic testing"
    },
    18: { // Insomnia
      title: "Chronic Insomnia Dismissed as Stress | Sleep Problems Ignored",
      description: "Insomnia affecting your life but doctor just says 'reduce stress'? Learn about underlying causes and how to get real answers. Free sleep health guide.",
      keywords: "insomnia dismissed, doctor won't help sleep, chronic fatigue ignored, sleep problems not taken seriously"
    }
  };

  // Get dynamic SEO based on current guide or default
  const getCurrentSeo = () => {
    const guideId = searchParams.get('guide');
    if (guideId && guideSeoData[guideId]) {
      return guideSeoData[guideId];
    }
    return {
      title: "CADENCE Compass | Free Health Education Guides & Articles",
      description: "FREE interactive health guides on thyroid, hormones, weight loss & sleep. Expert-written articles on medical gaslighting & patient advocacy. No login required.",
      keywords: "free health guides, health education, patient advocacy, thyroid health, hormonal health, weight management, medical gaslighting"
    };
  };

  const currentSeo = getCurrentSeo();

  return (
    <div className="compass-preview-page">
      <div 
        className="silk-background"
        style={{ backgroundImage: `url('/velvet-gold.png')` }}
      ></div>
      
      {/* Dynamic SEO Meta Tags - Long-tail keywords for each guide */}
      {isActiveRoute && (
      <Helmet>
        <title>{currentSeo.title}</title>
        <meta name="description" content={currentSeo.description} />
        <meta name="keywords" content={currentSeo.keywords} />
        {/* Prelaunch: Compass is one of the two indexable routes. */}
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <meta name="googlebot" content="index, follow" />
        <meta name="bingbot" content="index, follow" />
        <meta property="og:title" content={currentSeo.title} />
        <meta property="og:description" content={currentSeo.description} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="CADENCE Medical Consulting" />
        <meta property="og:url" content={`https://www.cadencemedicalconsulting.com/compass-preview${searchParams.get('guide') ? '?guide=' + searchParams.get('guide') : ''}`} />
        {/* Per-guide share-card if a specific guide is open; otherwise the
            site-wide default CADENCE card. Both render as 1200x630 PNGs
            via the /api/og endpoints. */}
        <meta property="og:image" content={
          (searchParams.get('article') || searchParams.get('guide'))
            ? `https://www.cadencemedicalconsulting.com/api/og/article/${searchParams.get('article') || searchParams.get('guide')}.png`
            : `https://www.cadencemedicalconsulting.com/api/og/default.png`
        } />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={currentSeo.title} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={currentSeo.title} />
        <meta name="twitter:description" content={currentSeo.description} />
        <meta name="twitter:image" content={
          (searchParams.get('article') || searchParams.get('guide'))
            ? `https://www.cadencemedicalconsulting.com/api/og/article/${searchParams.get('article') || searchParams.get('guide')}.png`
            : `https://www.cadencemedicalconsulting.com/api/og/default.png`
        } />
        <link rel="canonical" href={`https://www.cadencemedicalconsulting.com/compass-preview${searchParams.get('guide') ? '?guide=' + searchParams.get('guide') : ''}`} />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "CADENCE Compass — Free Health Education Guides",
            "description": "Free interactive health guides on thyroid, hormones, weight management, sleep, and patient advocacy. Expert-written by Dr. Lakia Stewart, DNP, FNP-C, WHNP-BC.",
            "url": "https://www.cadencemedicalconsulting.com/compass-preview",
            "isPartOf": {
              "@type": "WebSite",
              "name": "CADENCE Medical Consulting",
              "url": "https://www.cadencemedicalconsulting.com"
            },
            "publisher": {
              "@type": "Organization",
              "name": "CADENCE Medical Consulting, LLC",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.cadencemedicalconsulting.com/cadence-gold-plate.png"
              }
            },
            "author": {
              "@type": "Person",
              "name": "Lakia S. Stewart",
              "honorificSuffix": "DNP, FNP-C, WHNP-BC",
              "jobTitle": "Chief Medical Officer, CADENCE Medical Consulting, LLC"
            },
            "about": [
              { "@type": "MedicalCondition", "name": "Thyroid Disorders" },
              { "@type": "MedicalCondition", "name": "Hormonal Imbalance" },
              { "@type": "MedicalCondition", "name": "Low Testosterone" },
              { "@type": "MedicalCondition", "name": "Insomnia" },
              { "@type": "Thing", "name": "Medical Gaslighting" },
              { "@type": "Thing", "name": "Patient Advocacy" },
              { "@type": "Thing", "name": "Weight Management" }
            ]
          }
        `}</script>
      </Helmet>
      )}
      
      <div className="compass-preview-content">
        {/* Floating prev/next nav arrows so users aren't stuck on this page.
            Mirrors the curtain navigation arrows on /cadence-promise etc.
            Founder follow-up 2026-02: only render these on the standalone
            /compass-preview route — when this component renders inside the
            CurtainNavigation at /articles, the curtain's own arrows handle
            navigation; rendering BOTH sets caused two visual arrow pairs
            and the inner ones bounced to /membership instead of looping
            home. */}
        {location.pathname === '/compass-preview' && (
          <>
            <Link
              to="/membership"
              className="cp-side-arrow cp-side-arrow-left"
              aria-label="Back to Membership"
              data-testid="compass-prev-arrow"
            >
              <ChevronLeft size={28} strokeWidth={2.5} />
            </Link>
            <Link
              to="/home"
              className="cp-side-arrow cp-side-arrow-right"
              aria-label="Continue to Home"
              data-testid="compass-next-arrow"
            >
              <ChevronRight size={28} strokeWidth={2.5} />
            </Link>
          </>
        )}

        {/* Return-to-Welcome pill — visitors reach the Compass from the gated
            Welcome (Coming Soon) page, so give them a prominent way back at
            the top instead of only the footer CTA. Standalone route only;
            inside the curtain (/articles) the curtain arrows handle nav. */}
        {location.pathname === '/compass-preview' && (
          <header className="cp-header">
            <Link to="/" className="cp-back-link" data-testid="compass-return-welcome">
              <ArrowLeft size={18} strokeWidth={2.5} />
              Return to Welcome
            </Link>
          </header>
        )}

        {/* Header — REMOVED back-link so this page wears the same chrome
            as every other sister curtain page (1–7). Visitors navigate
            via the curtain arrows and the global header. */}

        {/* FOIL C EMBLEM — matches every other curtain page so the Compass
            reads as a CADENCE-authored experience, not a separate product. */}
        <section className="emblem-section" data-testid="compass-emblem">
          <div className="container">
            <div className="emblem-wrapper">
              <img
                src="/cadence-gold-foil.png"
                alt="CADENCE Medical Consulting"
                className="velvet-emblem-home"
              />
            </div>
          </div>
        </section>

        {/* TITLE — unified treatment shared with curtain pages 1–7:
            LuxeCompassWatermark behind the title, single h1 in white. */}
        <section className="unified-content-section" data-testid="compass-title-section">
          <div className="container">
            <LuxeCompassWatermark />
            <h1 className="unified-page-title">CADENCE Compass</h1>
          </div>
        </section>

        {/* ITALIC QUOTE — same Crimson Pro voice as the sister pages. */}
        <section className="unified-quote-section">
          <div className="container">
            <p className="unified-page-quote" style={{ fontStyle: 'italic' }}>
              Your guide to understanding complex health conditions.
            </p>
          </div>
        </section>
        
        {/* Preview Notice */}
        <div className="cp-preview-notice">
          <BookOpen size={20} />
          <span>Preview Mode: {unlockedGuides.length} guide{unlockedGuides.length !== 1 ? 's' : ''} + {unlockedArticles.length} article{unlockedArticles.length !== 1 ? 's' : ''} available. More coming with membership!</span>
        </div>
        
        {/* Navigation Tabs */}
        <div className="cp-nav">
          <button 
            className={`cp-nav-btn ${activeTab === 'guides' ? 'active' : ''}`}
            onClick={() => setActiveTab('guides')}
          >
            <Play size={20} />
            <span>Interactive<br />Guides</span>
            <div className="preview-badge">{unlockedGuides.length} Free</div>
          </button>
          <button 
            className={`cp-nav-btn ${activeTab === 'articles' ? 'active' : ''}`}
            onClick={() => setActiveTab('articles')}
          >
            <FileText size={20} />
            <span>Articles &<br />Insights</span>
            <div className="preview-badge">{unlockedArticles.length} Free</div>
          </button>
          <button className="cp-nav-btn disabled">
            <Map size={20} />
            <span>Deep<br />Dives</span>
            <div className="coming-badge">Coming Soon</div>
          </button>
        </div>
        
        {/* Guides Tab Content */}
        {activeTab === 'guides' && (
          <>
            {/* Unlocked Guides */}
            <section className="cp-guides-section">
              <h2>Available Now</h2>
              <div className="cp-guides-grid">
                {unlockedGuides.map(guide => (
                  <div 
                    key={guide.id}
                    className="cp-guide-card unlocked"
                    onClick={() => handleGuideClick(guide)}
                    data-testid={`guide-${guide.id}`}
                  >
                    <div 
                      className="cp-guide-image"
                      style={{ backgroundImage: `url(${guide.image})` }}
                    >
                      <div className="cp-play-btn">
                        <Play size={24} />
                      </div>
                    </div>
                    <div className="cp-guide-info">
                      <span className="cp-guide-category">{guide.topicCategory}</span>
                      <h3 className="cp-guide-title">{guide.title}</h3>
                      <p className="cp-guide-meta">{guide.slides?.length || 0} slides • {guide.readTime}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
            
            {/* Locked Guides Teaser */}
            <section className="cp-guides-section locked-section">
              <h2>Coming with Membership</h2>
              <p className="cp-locked-subtitle">Unlock {lockedGuides.length}+ additional guides when you become a member</p>
              <div className="cp-guides-grid">
                {lockedGuides.slice(0, 6).map(guide => (
                  <div 
                    key={guide.id}
                    className="cp-guide-card locked"
                    data-testid={`guide-locked-${guide.id}`}
                  >
                    <div 
                      className="cp-guide-image"
                      style={{ backgroundImage: `url(${guide.image})` }}
                    >
                      <div className="cp-lock-overlay">
                        <Lock size={32} />
                        <span>Members Only</span>
                      </div>
                    </div>
                    <div className="cp-guide-info">
                      <span className="cp-guide-category">{guide.topicCategory}</span>
                      <h3 className="cp-guide-title">{guide.title}</h3>
                    </div>
                  </div>
                ))}
              </div>
              
              {lockedGuides.length > 6 && (
                <p className="cp-more-count">+ {lockedGuides.length - 6} more guides</p>
              )}
            </section>
          </>
        )}
        
        {/* Articles Tab Content */}
        {activeTab === 'articles' && (
          <>
            {/* Unlocked Articles */}
            <section className="cp-guides-section">
              <h2>Available Now</h2>
              <div className="cp-guides-grid">
                {unlockedArticles.map(article => (
                  <div 
                    key={article.id}
                    className="cp-guide-card unlocked article-card"
                    onClick={() => handleArticleClick(article)}
                    data-testid={`article-${article.id}`}
                  >
                    <div 
                      className="cp-guide-image"
                      style={{ backgroundImage: `url(${article.image})` }}
                    >
                      <div className="cp-article-badge">
                        <FileText size={16} />
                        <span>Article</span>
                      </div>
                    </div>
                    <div className="cp-guide-info">
                      <span className="cp-guide-category">{article.topicCategory || article.category}</span>
                      <h3 className="cp-guide-title">{article.title}</h3>
                      <p className="cp-guide-meta">{article.readTime} read</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
            
            {/* Locked Articles Teaser */}
            <section className="cp-guides-section locked-section">
              <h2>Coming with Membership</h2>
              <p className="cp-locked-subtitle">Unlock {lockedArticles.length}+ additional articles when you become a member</p>
              <div className="cp-guides-grid">
                {lockedArticles.slice(0, 4).map(article => (
                  <div 
                    key={article.id}
                    className="cp-guide-card locked"
                    data-testid={`article-locked-${article.id}`}
                  >
                    <div 
                      className="cp-guide-image"
                      style={{ backgroundImage: `url(${article.image})` }}
                    >
                      <div className="cp-lock-overlay">
                        <Lock size={32} />
                        <span>Members Only</span>
                      </div>
                    </div>
                    <div className="cp-guide-info">
                      <span className="cp-guide-category">{article.topicCategory || article.category}</span>
                      <h3 className="cp-guide-title">{article.title}</h3>
                    </div>
                  </div>
                ))}
              </div>
              
              {lockedArticles.length > 4 && (
                <p className="cp-more-count">+ {lockedArticles.length - 4} more articles</p>
              )}
            </section>
          </>
        )}
        
        {/* CTA */}
        <section className="cp-cta-section">
          <h2>Ready for the Full Experience?</h2>
          <p>Join CADENCE to unlock all guides, articles, and personalized advocacy support.</p>
          <div className="cp-cta-buttons">
            <Link to="/contact" className="cp-cta-primary">
              Request Consultation
            </Link>
            <Link to="/" className="cp-cta-secondary" data-testid="compass-return-home-cta">
              Return to Main Site
            </Link>
          </div>
        </section>
        
        {/* Footer */}
        <footer className="cp-footer">
          <img src="/cadence-gold-foil.png" alt="CADENCE" className="cp-footer-logo" />
          <p className="cp-footer-text">CADENCE Medical Consulting, LLC</p>
        </footer>
      </div>
      
      {/* Slideshow Modal */}
      {selectedGuide && (
        <ArticleSlideshow 
          article={selectedGuide} 
          onClose={() => {
            setSelectedGuide(null);
            setOpenedFromUrl(false);
          }}
          startAtIntro={openedFromUrl}
        />
      )}
      
      {/* Article Reader Modal */}
      {selectedArticle && (
        <div className="cp-article-modal" onClick={() => setSelectedArticle(null)}>
          <div className="cp-article-content" onClick={e => e.stopPropagation()}>
            <button className="cp-article-close" onClick={() => setSelectedArticle(null)}>
              <X size={24} />
            </button>
            {/* Floating gold share button — matches guide pattern: single circle, native share, CADENCE believe tagline */}
            {(() => {
              const API_URL = process.env.REACT_APP_BACKEND_URL || '';
              const shareUrl = `${window.location.origin}/compass-preview?article=${selectedArticle.id}&utm_source=article_share&utm_content=${selectedArticle.id}`;
              const shareTagline = `CADENCE believes you deserve to know: ${selectedArticle.title}`;
              const trackShare = async (method) => {
                try {
                  await fetch(`${API_URL}/api/track-share`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      guide_id: selectedArticle.id,
                      guide_title: selectedArticle.title,
                      share_method: method
                    })
                  });
                } catch (err) {
                  // non-blocking
                }
              };
              const copyFallback = async () => {
                try {
                  await navigator.clipboard.writeText(`${shareTagline}\n\n${shareUrl}`);
                } catch {
                  const ta = document.createElement('textarea');
                  ta.value = `${shareTagline}\n\n${shareUrl}`;
                  document.body.appendChild(ta);
                  ta.select();
                  document.execCommand('copy');
                  document.body.removeChild(ta);
                }
                setLinkCopied(true);
                setTimeout(() => setLinkCopied(false), 2000);
                await trackShare('copy');
              };
              const handleShare = async () => {
                if (navigator.share) {
                  try {
                    await navigator.share({
                      title: selectedArticle.title,
                      text: shareTagline,
                      url: shareUrl
                    });
                    setLinkCopied(true);
                    setTimeout(() => setLinkCopied(false), 2000);
                    await trackShare('native');
                  } catch (err) {
                    if (err.name !== 'AbortError') {
                      await copyFallback();
                    }
                  }
                } else {
                  await copyFallback();
                }
              };
              return (
                <button
                  className={`cp-article-share-fab ${linkCopied ? 'success' : ''}`}
                  onClick={handleShare}
                  aria-label="Share this article"
                  data-testid="article-share-fab"
                  title="Share article"
                  type="button"
                >
                  {linkCopied ? <Check size={22} /> : <Share2 size={22} />}
                </button>
              );
            })()}
            <div className="cp-article-header">
              <span className="cp-article-category">{selectedArticle.topicCategory || selectedArticle.category}</span>
              <h2>{selectedArticle.title}</h2>
              {selectedArticle.subtitle && <p className="cp-article-subtitle">{selectedArticle.subtitle}</p>}
              <div className="cp-article-meta-info">
                <span>{selectedArticle.readTime} read</span>
                <span>•</span>
                {(() => {
                  const authorStr = selectedArticle.author || '';
                  // Split on first comma — everything before = name, after = credentials
                  const commaIdx = authorStr.indexOf(',');
                  if (commaIdx > 0) {
                    const name = authorStr.slice(0, commaIdx).trim();
                    const creds = authorStr.slice(commaIdx + 1).trim();
                    return (
                      <span className="cp-article-author">
                        <span className="cp-article-author-name">{name}</span>
                        <span className="cp-article-author-creds">{creds}</span>
                      </span>
                    );
                  }
                  return <span className="cp-article-author"><span className="cp-article-author-name">{authorStr}</span></span>;
                })()}
              </div>
            </div>
            {selectedArticle.image && (
              <img src={selectedArticle.image} alt={selectedArticle.title} className="cp-article-img" />
            )}
            <div className="cp-article-body">
              {/* Handle both string and array content formats */}
              {typeof selectedArticle.content === 'string' ? (
                <div className="cp-article-section">
                  {selectedArticle.content.split('\n\n').map((paragraph, idx) => {
                    const trimmed = paragraph.trim();
                    // Render `---` dividers as decorative gilded dotted rule
                    if (trimmed === '---') {
                      return <hr key={idx} />;
                    }
                    return (
                      <p key={idx} dangerouslySetInnerHTML={{ 
                        __html: trimmed
                          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                          .replace(/\n/g, '<br />') 
                      }} />
                    );
                  })}
                </div>
              ) : (
                selectedArticle.content?.map((section, idx) => (
                  <div key={idx} className="cp-article-section">
                    {section.heading && <h3>{section.heading}</h3>}
                    {section.text && <p>{section.text}</p>}
                    {section.items && (
                      <ul>
                        {section.items.map((item, i) => <li key={i}>{item}</li>)}
                      </ul>
                    )}
                  </div>
                ))
              )}
            </div>
            {/* Closing CADENCE section — copper tile with CTA below, same pattern as other pages */}
            <section className="cp-article-closing-tile-section">
              <div className="cp-article-closing-tile">
                <p className="cp-article-closing-tile-headline">
                  This is exactly what we do at CADENCE.
                </p>
                <p className="cp-article-closing-tile-body">
                  We review a cross-section of your encounters and care &mdash;
                  connecting the visits, labs, and specialist notes that span the
                  arc of your symptoms &mdash; and translate what the system leaves
                  unread into a clear, written plan you can act on.
                </p>
                <p className="cp-article-closing-signature">
                  <span>Clinical Advocacy</span>
                  <span>Decisive Empowerment</span>
                  <span>Navigating Care &amp; Expertise</span>
                </p>
              </div>
            </section>
            <div className="cp-article-footer">
              <span className="cp-article-footer-label">Ready to be heard?</span>
              <Link to="/contact" className="cp-cta-primary" onClick={() => setSelectedArticle(null)}>
                Request a Consultation →
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompassPreview;
